// Console logger bridge: allows parent to request live console forwarding (log, warn, error)
// Usage: parent posts { type: 'REQUEST_CONSOLE_LOGS' } to enable and { type: 'STOP_CONSOLE_LOGS' } to disable

export function getConsoleLoggerScript(): string {
  return `
    <script>
      (function() {
        var forwardingEnabled = false;
        var patched = false;
        var original = {
          log: console.log && console.log.bind ? console.log.bind(console) : console.log,
          info: console.info && console.info.bind ? console.info.bind(console) : console.info,
          debug: console.debug && console.debug.bind ? console.debug.bind(console) : console.debug,
          warn: console.warn && console.warn.bind ? console.warn.bind(console) : console.warn,
          error: console.error && console.error.bind ? console.error.bind(console) : console.error,
          trace: console.trace && console.trace.bind ? console.trace.bind(console) : console.trace,
          assert: console.assert && console.assert.bind ? console.assert.bind(console) : console.assert,
          group: console.group && console.group.bind ? console.group.bind(console) : console.group,
          groupCollapsed: console.groupCollapsed && console.groupCollapsed.bind ? console.groupCollapsed.bind(console) : console.groupCollapsed,
          groupEnd: console.groupEnd && console.groupEnd.bind ? console.groupEnd.bind(console) : console.groupEnd,
          table: console.table && console.table.bind ? console.table.bind(console) : console.table,
          dir: console.dir && console.dir.bind ? console.dir.bind(console) : console.dir
        };

        function safeSerialize(arg) {
          try {
            if (arg instanceof Error) {
              return { name: arg.name, message: arg.message, stack: arg.stack };
            }
            // Avoid circular refs
            var cache = new Set();
            var result = JSON.parse(JSON.stringify(arg, function(key, value) {
              if (typeof value === 'object' && value !== null) {
                if (cache.has(value)) return '[Circular]';
                cache.add(value);
              }
              return value;
            }));
            cache.clear();
            return result;
          } catch (e) {
            try { return String(arg); } catch { return '[Unserializable]'; }
          }
        }

        function post(method, argsArray) {
          try {
            if (window.parent && window.parent !== window && forwardingEnabled) {
              window.parent.postMessage({
                type: 'console',
                method: method,
                args: (argsArray || []).map(safeSerialize),
                timestamp: new Date().toISOString()
              }, '*');
            }
          } catch (err) {
            // Swallow
          }
        }

        function patchConsole() {
          if (patched) return;
          patched = true;

          var methods = ['log','info','debug','warn','error','trace'];
          methods.forEach(function(method) {
            try {
              var orig = original[method] || console[method];
              if (typeof orig !== 'function') return;
              console[method] = function() {
                try { post(method, Array.prototype.slice.call(arguments)); } catch {}
                return orig.apply(console, arguments);
              };
            } catch {}
          });

          // Special-case: assert only posts when the assertion fails
          try {
            var origAssert = original.assert || console.assert;
            if (typeof origAssert === 'function') {
              console.assert = function(condition) {
                var args = Array.prototype.slice.call(arguments, 1);
                try { if (!condition) post('assert', args); } catch {}
                return origAssert.apply(console, arguments);
              };
            }
          } catch {}

          // Optional: group and table forwarding
          ['group','groupCollapsed','groupEnd','table','dir'].forEach(function(method) {
            try {
              var orig = original[method] || console[method];
              if (typeof orig !== 'function') return;
              console[method] = function() {
                try { post(method, Array.prototype.slice.call(arguments)); } catch {}
                return orig.apply(console, arguments);
              };
            } catch {}
          });
        }

        function enableForwarding() {
          forwardingEnabled = true;
          patchConsole();
          // Notify parent that forwarding is enabled
          post('log', ['[ConsoleLogger] Forwarding enabled']);
        }

        function disableForwarding() {
          forwardingEnabled = false;
          // We keep patched methods but guard by forwardingEnabled flag
          post('log', ['[ConsoleLogger] Forwarding disabled']);
        }

        window.addEventListener('message', function(event) {
          try {
            var data = event && event.data;
            if (!data || typeof data !== 'object') return;
            if (data.type === 'REQUEST_CONSOLE_LOGS') {
              enableForwarding();
            } else if (data.type === 'STOP_CONSOLE_LOGS') {
              disableForwarding();
            }
          } catch (e) {
            // ignore
          }
        });

        // Auto-enable by default; still allow parent to disable later
        try { enableForwarding(); } catch {}

        // Optional: also auto-enable via query flag
        try {
          if (new URLSearchParams(window.location.search).get('kulpConsole') === '1') {
            enableForwarding();
          }
        } catch {}
      })();
    </script>
  `;
}

export type GetConsoleLoggerScript = () => string;
